import React, { useEffect, useRef, useState } from 'react';

export interface RowAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface RowActionsMenuProps {
  actions: RowAction[];
  title?: string;
}

/* Geschätzte Höhe pro Menüeintrag für die Flip-Entscheidung am Viewport-Rand —
   das Menü ist zu diesem Zeitpunkt noch nicht gerendert. */
const EINTRAG_HOEHE = 33;

const RowActionsMenu: React.FC<RowActionsMenuProps> = ({ actions, title }) => {
  const [pos, setPos] = useState<{ top: number; left: number; nachOben: boolean } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pos) return;
    const schliessen = () => setPos(null);
    const klickAussen = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setPos(null);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPos(null);
    };
    document.addEventListener('mousedown', klickAussen);
    document.addEventListener('keydown', esc);
    /* capture: auch Scrollen im table-container (overflow-x) schließt das Menü,
       sonst bleibt es fix an der alten Viewport-Position stehen */
    window.addEventListener('scroll', schliessen, true);
    window.addEventListener('resize', schliessen);
    return () => {
      document.removeEventListener('mousedown', klickAussen);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', schliessen, true);
      window.removeEventListener('resize', schliessen);
    };
  }, [pos]);

  if (actions.length === 0) return null;

  const umschalten = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (pos) {
      setPos(null);
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    const menueHoehe = actions.length * EINTRAG_HOEHE + 10;
    const nachOben = window.innerHeight - r.bottom < menueHoehe && r.top > menueHoehe;
    setPos({
      top: nachOben ? r.top - 4 : r.bottom + 4,
      left: r.right,
      nachOben,
    });
  };

  return (
    <div className="k-rowmenu" ref={wrapRef}>
      <button
        type="button"
        className="k-rowmenu__trigger"
        title={title ?? 'Weitere Aktionen'}
        aria-haspopup="menu"
        aria-expanded={pos !== null}
        onClick={umschalten}
      >
        &#8943;
      </button>
      {pos && (
        <div
          className="k-rowmenu__list"
          role="menu"
          style={{
            top: pos.top,
            left: pos.left,
            transform: `translateX(-100%)${pos.nachOben ? ' translateY(-100%)' : ''}`,
          }}
        >
          {actions.map((a, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              disabled={a.disabled}
              className={'k-rowmenu__item' + (a.danger ? ' is-danger' : '')}
              onClick={(e) => {
                e.stopPropagation();
                setPos(null);
                a.onClick();
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RowActionsMenu;
